import { supabase } from '../api/supabase'

// Tiered ("阶梯") pricing helpers shared by every rate-card system. The customer side
// (pricing_rate_tiers) and the supplier side (supplier_rate_tiers) have the identical shape, so
// the editor component (components/pricing/TierEditor.tsx) and this persistence code are written
// once, parametrised by table name -- not supplier-specific.

export type TierTable = 'pricing_rate_tiers' | 'supplier_rate_tiers'

export interface TierDraft {
  _key: string // stable React key for rows that have no db id yet
  tier_from: number
  tier_to: number | null // null = open-ended top tier ("500+")
  unit_price: number
}

export function newTierDraft(prev?: TierDraft): TierDraft {
  // Continue where the previous tier stopped; if it was open-ended, close it at the same
  // boundary is the caller's job (TierEditor does it) -- here we only pick a sensible start.
  const from = prev ? (prev.tier_to ?? prev.tier_from + 1) : 0
  return { _key: crypto.randomUUID(), tier_from: from, tier_to: null, unit_price: 0 }
}

// Returns a human-readable error for the first problem found, or null if the set is valid.
// Rules mirror the table CHECK (tier_to > tier_from) plus what the DB can't express cheaply:
// no overlaps, and only the highest tier may be open-ended. Gaps are NOT an error (a gap just
// means "no tiered price for that quantity band"); see tierGaps() for the warning.
export function validateTiers(tiers: TierDraft[]): string | null {
  const sorted = [...tiers].sort((a, b) => a.tier_from - b.tier_from)
  for (let i = 0; i < sorted.length; i++) {
    const t = sorted[i]
    if (!Number.isFinite(t.tier_from) || t.tier_from < 0) return `Tier ${i + 1}: "From" must be 0 or more.`
    if (t.tier_to !== null && !(t.tier_to > t.tier_from)) return `Tier starting at ${t.tier_from}: "To" must be greater than "From".`
    if (!Number.isFinite(t.unit_price) || t.unit_price < 0) return `Tier starting at ${t.tier_from}: price must be 0 or more.`
    const next = sorted[i + 1]
    if (next) {
      if (t.tier_to === null) return `Tier starting at ${t.tier_from} is open-ended, so no tier can come after it.`
      if (next.tier_from < t.tier_to) return `Tiers overlap: ${t.tier_from}–${t.tier_to} and ${next.tier_from}+.`
    }
  }
  return null
}

// Quantity bands with no tier (e.g. tiers 0–10 and 20+ leave 10–20 uncovered).
export function tierGaps(tiers: TierDraft[]): string[] {
  const sorted = [...tiers].sort((a, b) => a.tier_from - b.tier_from)
  const gaps: string[] = []
  for (let i = 0; i < sorted.length - 1; i++) {
    const to = sorted[i].tier_to
    if (to !== null && sorted[i + 1].tier_from > to) gaps.push(`${to}–${sorted[i + 1].tier_from}`)
  }
  return gaps
}

export async function loadTiers(table: TierTable, lineIds: string[]): Promise<Map<string, TierDraft[]>> {
  const byLine = new Map<string, TierDraft[]>()
  if (lineIds.length === 0) return byLine
  const { data, error } = await supabase.from(table).select('id, rate_line_id, tier_from, tier_to, unit_price')
    .in('rate_line_id', lineIds).order('tier_from')
  if (error) throw error
  for (const r of data || []) {
    const list = byLine.get(r.rate_line_id) || []
    list.push({ _key: r.id, tier_from: Number(r.tier_from), tier_to: r.tier_to === null ? null : Number(r.tier_to), unit_price: Number(r.unit_price) })
    byLine.set(r.rate_line_id, list)
  }
  return byLine
}

// Replace-all: delete the line's tiers, insert the new set. Delete first so re-entering the same
// tier_from values can never trip the (rate_line_id, tier_from) unique constraint.
export async function saveTiers(table: TierTable, lineId: string, tiers: TierDraft[]): Promise<void> {
  const { error: delErr } = await supabase.from(table).delete().eq('rate_line_id', lineId)
  if (delErr) throw delErr
  if (tiers.length === 0) return
  const rows = tiers.map(t => ({ rate_line_id: lineId, tier_from: t.tier_from, tier_to: t.tier_to, unit_price: t.unit_price }))
  const { error: insErr } = await supabase.from(table).insert(rows)
  if (insErr) throw insErr
}
